<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import MasterLayout from "~/layouts/MasterLayout.vue";
import {usePageStore} from "~/plugins/store/page";
import {type Contact, createDefaultContact} from "~/types/Page";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";

export default defineComponent({
  components: {LoadingDialog, MasterLayout},
  setup() {
    useHead({
      title: '24Broker | Contactează-ne',
    });
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const pageStore = usePageStore();
    const contactData = ref<Contact>(createDefaultContact());
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se incarcă...');

      const tmpContactData = await pageStore.getContactData();
      if (tmpContactData) {
        contactData.value = tmpContactData;
      }
      showLoadingDialog.value = false;
    });

    return {
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      contactData
    }
  }
});
</script>

<template>
  <master-layout class="static">
    <v-container class="content-container background-gradient">
      <v-container fluid class="component-container-1400mw">
        <v-container class="background-surface" style="max-width: 1200px;">
          <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
          <!-- Content -->
          <v-card min-height="500" v-if="contactData && contactData.company" flat rounded="0">
            <v-card-title>
              <h1 class="text-center">Contact</h1>
            </v-card-title>

            <v-divider></v-divider>

            <v-row class="mt-3">
              <v-col cols="12" sm="6" md="6" class="d-flex align-center flex-column">
                <div>
                  <h3 class="font-weight-bold">
                    <v-icon size="x-large" icon="mdi-office-building-marker"></v-icon>
                    {{ contactData.company.company_name }}
                  </h3>
                  <div class="my-2">
                    <p v-if="contactData.company.company_address" class="font-weight-regular">
                      <strong>Adresa: </strong>{{ contactData.company.company_address }}
                    </p>
                    <p v-if="contactData.company.company_website" class="font-weight-regular">
                      <strong>Website: </strong>
                      <NuxtLink :to="contactData.company.company_website" rel="noopener" target="_blank">
                        {{ contactData.company.company_website }}
                      </NuxtLink>
                    </p>
                    <p v-if="contactData.company.company_email" class="font-weight-regular">
                      <strong>Email: </strong>
                      <NuxtLink :to="'mailto:' + contactData.company.company_email">{{
                          contactData.company.company_email
                        }}
                      </NuxtLink>
                    </p>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="6" class="d-flex align-center flex-column ">
                <div>
                  <h3 class="font-weight-bold">
                    <v-icon size="x-large" icon="mdi-badge-account"></v-icon>
                    {{ contactData.contact_first_name }} {{ contactData.contact_last_name }}
                  </h3>
                  <div class="my-2">
                    <p v-if="contactData.contact_phone_1" class="font-weight-regular">
                      <strong>Telefon: </strong>{{ contactData.contact_phone_1 }}
                    </p>
                    <p v-if="contactData.contact_phone_2" class="font-weight-regular">
                      <strong>Telefon Secundar: </strong>{{ contactData.contact_phone_2 }}
                    </p>
                    <p v-if="contactData.contact_email" class="font-weight-regular">
                      <strong>Email: </strong>
                      <NuxtLink :to="'mailto:' + contactData.contact_email">{{ contactData.contact_email }}</NuxtLink>
                    </p>
                    <p v-if="contactData.contact_skype" class="font-weight-regular">
                      <strong>Skype: </strong>{{ contactData.contact_skype }}
                    </p>
                    <p v-if="contactData.contact_address" class="font-weight-regular">
                      <strong>Adresa: </strong>{{ contactData.contact_address }}
                    </p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Undefined page -->
          <v-card v-else-if="(!contactData || !contactData.company) && !showLoadingDialog"
                  class="d-flex flex-column justify-center align-center" height="500px" color="transparent" flat>
            <div class="fw-500 fs-50">
              Pagini nedefinită
            </div>
          </v-card>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>


<style scoped>

</style>